import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Check, Leaf, TreePine, Droplets, Waves, CarFront, Sun, Wrench, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSite } from "@/context/SiteContext";

const iconMap = { Leaf, TreePine, Droplets, Waves, CarFront, Sun, Wrench };

// Fallback SVG for service images
const FALLBACK_SERVICE_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect fill='%232D5A3D' width='400' height='300'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='white' font-size='16' font-family='sans-serif'%3ELes Entretiens Élites%3C/text%3E%3C/svg%3E";

export const ServicesSection = () => {
  const { services } = useSite();
  const [imageErrors, setImageErrors] = useState({});
  
  const featured = services.filter((s) => s.featured);
  const others = services.filter((s) => !s.featured);

  const scrollTo = () => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });

  const handleImageError = (serviceId, e) => {
    e.target.onerror = null;
    e.target.src = FALLBACK_SERVICE_IMAGE;
    setImageErrors(prev => ({ ...prev, [serviceId]: true }));
  };

  return (
    <section id="services" className="bg-[#F5F5F0] py-16 md:py-24" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="services-title">Nos services</h2>
          <p className="text-base lg:text-lg text-gray-600 max-w-2xl mx-auto">
            Des services d'entretien extérieur complets pour garder votre propriété en parfait état, toute la saison.
          </p>
        </div>

        {/* Featured Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {featured.map((service) => {
            const Icon = iconMap[service.icon] || Wrench;
            return (
              <Card 
                key={service.id} 
                className="bg-white border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col" 
                data-testid={`service-card-${service.id}`}
              >
                {service.image && (
                  <div className="h-48 overflow-hidden bg-gray-100">
                    <img 
                      src={service.image} 
                      alt={`Service ${service.name} - Les Entretiens Élites`}
                      className="w-full h-full object-cover"
                      width={400}
                      height={192}
                      loading="lazy"
                      decoding="async"
                      onError={(e) => handleImageError(service.id, e)}
                    />
                  </div>
                )}
                <CardContent className="flex-1 p-5">
                  <div className="flex items-start gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4.5 h-4.5 text-[#2D5A3D]" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-base leading-snug pt-1">{service.name}</h3>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed mb-2">{service.description}</p>
                  {service.sub_services?.length > 0 && (
                    <ul className="space-y-1 mt-2 pt-2 border-t border-gray-100">
                      {service.sub_services.map((sub) => (
                        <li key={`${service.id}-${sub}`} className="flex items-center gap-2 text-sm text-gray-700">
                          <Check className="w-3.5 h-3.5 text-[#2D5A3D] flex-shrink-0" />{sub}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Other services as pills */}
        {others.length > 0 && (
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-3">Nous offrons aussi :</p>
            <div className="flex flex-wrap justify-center gap-2">
              {others.map((s) => (
                <span key={s.id} className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm text-gray-700 font-medium">
                  {s.name}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="text-center mt-10">
          <Button onClick={scrollTo} size="lg" className="bg-[#2D5A3D] hover:bg-[#234A30] text-white px-8" data-testid="services-cta">
            Demander une soumission gratuite <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </section>
  );
};
